import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/common/SEOHead';
import { OFFER } from '../data/siteOffer';

const questions = [
  { question: 'Hur lång tid tar en enkel företagshemsida?', answer: `${OFFER.sv.timing} Exakt leveransdatum bekräftas i den skriftliga offerten.` },
  {
    question: 'Vad kostar webbhjälpen?',
    answer: 'Som riktmärke kostar en enkel ny företagshemsida cirka 4 900 kr. Mindre ändringar kostar mindre och mer omfattande arbete kostar mer. Slutpris och eventuell moms framgår i den skriftliga offerten innan arbetet börjar.',
  },
  {
    question: 'Vad ingår i riktpriset för en företagshemsida?',
    answer: OFFER.sv.included.join(' '),
  },
  {
    question: 'Kan StartaWebb förbättra en hemsida som redan finns?',
    answer: 'Ja. Det kan handla om mobilvisning, innehåll, kontaktvägar, formulär, domänkoppling och mindre tekniska problem. Skicka länken och beskriv vad som känns fel eller saknas.',
  },
  {
    question: 'Kan StartaWebb slutföra ett projekt byggt med Lovable, Cursor eller ChatGPT?',
    answer: 'Ja, när projektet går att avgränsa och fortsätta på ett rimligt sätt. Vanliga uppdrag gäller Firebase, inloggning, formulär, databaser, mobilvisning, Vercel, domän och lansering. Först bedöms det som redan finns. Om en ombyggnad eller ett större team krävs får du veta det innan arbete beställs.',
  },
  {
    question: 'Behöver jag förstå tekniken för att skicka en förfrågan?',
    answer: 'Nej. Beskriv vad som finns i dag, vad som inte fungerar och vad användaren ska kunna göra när projektet är klart. En publicerad länk eller förhandsvisning är ofta tillräcklig i första steget.',
  },
  {
    question: 'Kan StartaWebb bygga boknings-, admin- eller rapportfunktioner?',
    answer: 'Ja, mindre och tydligt avgränsade funktioner kan byggas, till exempel offertförfrågningar, bokningsflöden, enkel administration, statusar, fotodokumentation och PDF-rapporter. Större system delas upp i en första version.',
  },
  {
    question: 'Vad behövs från kunden?',
    answer: 'För en ny sida behövs korrekta företagsuppgifter och det material som finns, till exempel logotyp, bilder och information om tjänsterna. För ett befintligt projekt behövs först en länk och en beskrivning. Kod- och kontotillgång ordnas först när uppdraget har bedömts.',
  },
  {
    question: 'Ingår domän och hosting?',
    answer: OFFER.sv.external,
  },
  {
    question: 'Används AI i utvecklingen?',
    answer: 'Moderna utvecklingsverktyg, inklusive AI-stöd, kan användas för att arbeta snabbare. StartaWebb ansvarar fortfarande för att den överenskomna leveransen granskas, testas och fungerar enligt omfattningen. Kunden betalar för en fungerande lösning, inte för hur många manuella tangenttryckningar som används.',
  },
  {
    question: 'Vilka projekt tas inte in som små uppdrag?',
    answer: 'Bank-, vård-, betalnings- och andra säkerhetskritiska system tas inte in som enkla småprojekt. Detsamma gäller stora plattformar utan tydlig första leverans eller projekt där kunden saknar rättigheter till kod, konton eller material.',
  },
  {
    question: 'Går det att garantera fler kunder eller en placering på Google?',
    answer: 'Nej. StartaWebb kan bygga en tydlig, mobilanpassad och tekniskt korrekt sida, men lovar inte en viss försäljning, ett visst antal kunder eller en bestämd sökplacering.',
  },
  {
    question: 'Bygger StartaWebb Android-appar?',
    answer: 'Ja. Mindre Android-appar och prototyper kan utvecklas när funktionerna är tydliga. Exempel är BostadBevis, FakturaVakt, SnapHandover och International APN Settings.',
  },
  {
    question: 'Vem äger hemsidan och kontona efter leverans?',
    answer: 'Domän, e-post, hosting och andra externa konton bör normalt stå i kundens namn. Vad som överlämnas och vilka rättigheter som ingår skrivs tydligt i offerten före start.',
  },
  {
    question: 'Kan StartaWebb hjälpa med texter, bilder och flera språk?',
    answer: 'Ja, struktur, textbearbetning, bildval och flera språk kan ingå när det avtalas. Kunden ansvarar för att eget material får användas, och eventuella köpta bilder eller licenser anges separat.',
  },
  {
    question: 'Hur fungerar betalningen och ändringar efter start?',
    answer: 'Betalningsupplägg, förfallodatum och eventuell uppdelning står i offerten. Fel som hör till den överenskomna leveransen rättas. Nya funktioner eller ändringar utanför omfattningen får ett separat pris innan de görs.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: questions.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
};

export default function FAQ() {
  return (
    <>
      <SEOHead
        title="Vanliga frågor om hemsidor och AI-projekt | StartaWebb"
        description="Svar om priser, hemsidor, Lovable- och Cursor-projekt, Firebase, boknings- och adminfunktioner, Android, ägande och hur AI används i utvecklingen."
        canonical="https://startawebb.se/faq"
        keywords="fixa Lovable projekt, AI byggd hemsida, webbhjälp småföretag, Firebase hjälp, hemsida riktpris, Android utveckling"
        structuredData={faqSchema}
      />

      <section className="relative overflow-hidden bg-gradient-to-br from-sky-50 via-white to-cyan-50 py-20">
        <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-cyan-200/35 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-4 text-center">
          <p className="mb-4 inline-flex rounded-full border border-sky-200 bg-white px-4 py-2 text-sm font-bold text-sky-800">Raka svar före start</p>
          <h1 className="mb-5 text-4xl font-extrabold tracking-tight text-slate-950 md:text-5xl">Vanliga frågor</h1>
          <p className="text-lg leading-relaxed text-slate-600">Omfattning och pris bestäms utifrån det arbete som faktiskt behövs. Här finns svar för både nya hemsidor, befintliga projekt och lösningar som har byggts med AI-verktyg.</p>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-5xl gap-5 px-4 md:grid-cols-2">
          {questions.map((item, index) => (
            <article key={item.question} className="rounded-2xl border border-slate-200 bg-slate-50 p-6 transition hover:border-sky-200 hover:bg-white hover:shadow-lg md:p-7">
              <p className="mb-4 text-sm font-extrabold tracking-[0.16em] text-sky-600">{String(index + 1).padStart(2, '0')}</p>
              <h2 className="mb-3 text-xl font-bold text-slate-950">{item.question}</h2>
              <p className="leading-relaxed text-slate-600">{item.answer}</p>
            </article>
          ))}
        </div>
        <div className="flex flex-col items-center justify-center gap-4 pt-10 sm:flex-row">
          <Link to="/contact" className="inline-flex rounded-xl bg-sky-700 px-8 py-4 font-bold text-white shadow-lg shadow-sky-700/15 transition hover:bg-sky-800">Beskriv ditt behov</Link>
          <Link to="/ai-projekt" className="inline-flex rounded-xl border border-violet-200 bg-violet-50 px-8 py-4 font-bold text-violet-800 transition hover:bg-violet-100">Hjälp med AI-projekt</Link>
        </div>
      </section>
    </>
  );
}
